import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Flame, Goal, LineChart, Shield, Trophy, Users, type LucideIcon } from 'lucide-react';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { AdminModulePage, PageHero } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { buildLeagueInsights, type LeagueInsightGame } from '../services/leagueInsightsService';

export function ReportsPage() {
  const db = useStore((d) => d);
  const insights = useMemo(() => buildLeagueInsights(db), [db]);
  const hasData = insights.metrics.some((metric) => Number(metric.value) > 0);

  return (
    <AdminModulePage>
      <PageHero
        eyebrow="Grow the league"
        title="League intelligence"
        description="Deterministic reads from scoring, standings, teams, players, goalies, and recent results."
        icon={LineChart}
        accent="arc"
        status={{
          label: `${insights.recentFinals.length} recent final${insights.recentFinals.length === 1 ? '' : 's'}`,
          tone: insights.recentFinals.length > 0 ? 'ok' : 'info',
        }}
      />

      {!hasData ? (
        <GuidedEmpty
          icon={LineChart}
          title="Insights need scored games"
          body="League intelligence appears after schedules, rosters, and scorekeeper results are available."
          action={{ to: '/admin/seasons', label: 'Open seasons' }}
        />
      ) : (
        <>
          <section className="insights-kpis">
            {insights.metrics.map((metric) => (
              <div key={metric.label} className="insights-kpi">
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
                <p>{metric.detail}</p>
              </div>
            ))}
          </section>

          <section className="grid grid--2">
            <div className="card">
              <InsightCardHead icon={Flame} title="Hot players" subtitle="Point leaders across active events." />
              {insights.hotPlayers.length === 0 ? (
                <GuidedEmpty icon={Users} title="No player leaders yet" body="Player leaders appear after goals and assists are recorded." />
              ) : (
                <div className="insights-list">
                  {insights.hotPlayers.map((player, index) => (
                    <Link key={`${player.eventId}-${player.playerId}`} to={`/events/${player.eventSlug}/players/${player.playerId}`} className="insights-row">
                      <span className="insights-rank">{index + 1}</span>
                      <div>
                        <strong>{player.playerName}</strong>
                        <p>{player.teamName ?? 'Unassigned'} - {player.eventName}</p>
                      </div>
                      <div className="insights-stat"><strong>{player.points}</strong><span>PTS</span></div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="card">
              <InsightCardHead icon={Trophy} title="Hot teams" subtitle="Standings pressure and current team form." />
              {insights.hotTeams.length === 0 ? (
                <GuidedEmpty icon={Trophy} title="No team form yet" body="Team form appears after standings are available." />
              ) : (
                <div className="insights-list">
                  {insights.hotTeams.map((team) => (
                    <Link key={`${team.eventId}-${team.teamId}`} to={`/events/${team.eventSlug}/teams/${team.teamId}`} className="insights-row">
                      <span className="insights-color" style={{ background: team.primaryColor }} />
                      <div>
                        <strong>{team.teamName}</strong>
                        <p>{team.trendLabel} - {team.eventName}</p>
                      </div>
                      <div className="insights-stat"><strong>{team.points}</strong><span>PTS</span></div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="grid grid--2">
            <GameInsightCard title="Close games" subtitle="Finals decided by one goal or less." rows={insights.closeGames} />
            <GameInsightCard title="Comeback games" subtitle="Winners that trailed earlier in the game." rows={insights.comebackGames} />
          </section>

          <section className="grid grid--2">
            <div className="card">
              <InsightCardHead icon={Shield} title="Goalie leaders" subtitle="Save percentage and goals against from recorded goalie data." />
              {insights.goalieLeaders.length === 0 ? (
                <GuidedEmpty icon={Shield} title="No goalie leaders yet" body="Goalie intelligence appears when score sheets include goalies or saves." />
              ) : (
                <div className="table-scroll">
                  <table className="table insights-table">
                    <thead><tr><th>Goalie</th><th>Team</th><th>GP</th><th>W</th><th>SV%</th><th>GAA</th><th>SO</th></tr></thead>
                    <tbody>
                      {insights.goalieLeaders.map((goalie) => (
                        <tr key={`${goalie.eventId}-${goalie.playerId}`}>
                          <td><Link to={`/events/${goalie.eventSlug}/players/${goalie.playerId}`}><strong>{goalie.playerName}</strong></Link></td>
                          <td>{goalie.teamId ? <Link to={`/events/${goalie.eventSlug}/teams/${goalie.teamId}`}>{goalie.teamName ?? '-'}</Link> : goalie.teamName ?? '-'}</td>
                          <td>{goalie.gamesPlayed}</td>
                          <td>{goalie.wins}</td>
                          <td>{formatPct(goalie.savePct)}</td>
                          <td>{formatNumber(goalie.gaa)}</td>
                          <td>{goalie.shutouts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <InsightCardHead icon={Activity} title="Penalty trends" subtitle="Teams carrying the most penalty minutes." />
              {insights.penaltyTeams.length === 0 ? (
                <GuidedEmpty icon={Activity} title="No penalty trend yet" body="Penalty trends appear after penalty events are recorded." />
              ) : (
                <div className="insights-list">
                  {insights.penaltyTeams.map((team) => (
                    <Link key={`${team.eventId}-${team.teamId}`} to={`/events/${team.eventSlug}/teams/${team.teamId}`} className="insights-row">
                      <span className="insights-color" style={{ background: team.primaryColor }} />
                      <div>
                        <strong>{team.teamName}</strong>
                        <p>{team.eventName} - {team.goalsFor}-{team.goalsAgainst}</p>
                      </div>
                      <div className="insights-stat"><strong>{team.penaltyMinutes}</strong><span>PIM</span></div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="grid grid--2">
            <div className="card">
              <InsightCardHead icon={Goal} title="Division competitiveness" subtitle="Tight races, point spread, and one-goal games." />
              {insights.divisionCompetitiveness.length === 0 ? (
                <GuidedEmpty icon={Goal} title="No division read yet" body="Division competitiveness appears after standings and final scores exist." />
              ) : (
                <div className="insights-list">
                  {insights.divisionCompetitiveness.map((division) => (
                    <div key={`${division.eventId}-${division.divisionId}`} className="insights-row">
                      <span className="insights-rank">{division.teams}</span>
                      <div>
                        <strong>{division.divisionName}</strong>
                        <p>{division.eventName} - {division.label}</p>
                      </div>
                      <div className="insights-stat"><strong>{division.pointsSpread}</strong><span>spread</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card">
              <InsightCardHead icon={LineChart} title="Scoring trends" subtitle="Goals and penalty minutes by recent game dates." />
              {insights.scoringTrend.length === 0 ? (
                <GuidedEmpty icon={LineChart} title="No trend yet" body="Scoring trends appear as final games accumulate." />
              ) : (
                <div className="insights-trend">
                  {insights.scoringTrend.map((point) => (
                    <div key={point.key} className="insights-trend__row">
                      <span>{point.label}</span>
                      <div className="insights-trend__bar"><i style={{ width: `${Math.max(6, Math.min(100, point.goals * 8))}%` }} /></div>
                      <strong>{point.goals} G</strong>
                      <em>{point.penaltyMinutes} PIM</em>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <InsightCardHead icon={Trophy} title="Recent finals" subtitle="Latest official results across the league." />
            {insights.recentFinals.length === 0 ? (
              <GuidedEmpty icon={Trophy} title="No finals yet" body="Finals appear after scorekeepers submit official results." />
            ) : (
              <div className="insights-game-grid">
                {insights.recentFinals.map((game) => <InsightGameLink key={`${game.eventId}-${game.gameId}`} game={game} />)}
              </div>
            )}
          </section>
        </>
      )}
    </AdminModulePage>
  );
}

function InsightCardHead({ icon: Icon, title, subtitle }: { icon: LucideIcon; title: string; subtitle: string }) {
  return (
    <div className="card__head">
      <div>
        <h3 className="card__title">
          <Icon size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
          {title}
        </h3>
        <p className="card__sub">{subtitle}</p>
      </div>
    </div>
  );
}

function GameInsightCard({ title, subtitle, rows }: { title: string; subtitle: string; rows: LeagueInsightGame[] }) {
  return (
    <div className="card">
      <InsightCardHead icon={Trophy} title={title} subtitle={subtitle} />
      {rows.length === 0 ? (
        <GuidedEmpty icon={Trophy} title={`No ${title.toLowerCase()} yet`} body="This read appears as more official finals are recorded." />
      ) : (
        <div className="insights-game-grid">
          {rows.map((game) => <InsightGameLink key={`${game.eventId}-${game.gameId}`} game={game} />)}
        </div>
      )}
    </div>
  );
}

function InsightGameLink({ game }: { game: LeagueInsightGame }) {
  return (
    <Link to={`/events/${game.eventSlug}/games/${game.gameId}`} className="insights-game">
      <div>
        <strong>{game.homeTeamName}</strong> {game.homeScore}-{game.awayScore} <strong>{game.awayTeamName}</strong>
        <p>{game.eventName}{game.divisionName ? ` - ${game.divisionName}` : ''}</p>
      </div>
      <span className="chip chip--accent">{game.label}</span>
    </Link>
  );
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return value.toFixed(3).replace(/^0/, '');
}
